import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

// The settings rail. Its own module so the route titles and the command
// palette read the labels without loading the lazily split settings page.
// Every settings page is admin-only through the `/settings` rule in
// ROUTE_MODULES, so no item carries its own role gate.

export interface SettingsNavItem {
  path: string;
  /** The one name of the page: rail, page title, breadcrumb and document title. */
  label: MessageDescriptor;
  /** Words people search for that the label does not contain: matched by the rail filter and the command palette. */
  keywords: MessageDescriptor[];
}

export interface SettingsNavGroup {
  label: MessageDescriptor;
  items: SettingsNavItem[];
}

export const NAV_GROUPS: SettingsNavGroup[] = [
  {
    label: msg({ id: "settings.navGroup.general", message: "General" }),
    items: [
      {
        path: "/settings/organisation",
        label: msg({ id: "settings.nav.organisation", message: "Organisation" }),
        keywords: [
          msg({ id: "settings.keywords.organisation.timezone", message: "timezone" }),
          msg({ id: "settings.keywords.organisation.logo", message: "logo" }),
          msg({ id: "settings.keywords.organisation.address", message: "address" }),
        ],
      },
      {
        path: "/settings/childcare",
        label: msg({ id: "settings.nav.childcares", message: "Childcare locations" }),
        keywords: [
          msg({ id: "settings.keywords.childcare.sites", message: "sites" }),
          msg({ id: "settings.keywords.childcare.openingHours", message: "opening hours" }),
          msg({ id: "settings.keywords.childcare.canton", message: "canton" }),
        ],
      },
      {
        path: "/settings/groups",
        label: msg({ id: "settings.nav.groups", message: "Groups" }),
        keywords: [
          msg({ id: "settings.keywords.groups.rooms", message: "rooms" }),
          msg({ id: "settings.keywords.groups.ageRange", message: "age range" }),
          msg({ id: "settings.keywords.groups.places", message: "places" }),
        ],
      },
      {
        path: "/settings/access-roles",
        label: msg({ id: "settings.nav.accessRoles", message: "Access roles" }),
        keywords: [
          msg({ id: "settings.keywords.accessRoles.permissions", message: "permissions" }),
          msg({ id: "settings.keywords.accessRoles.modules", message: "modules" }),
          msg({ id: "settings.keywords.accessRoles.webAccess", message: "web access" }),
        ],
      },
      {
        path: "/settings/devices",
        label: msg({ id: "settings.nav.devices", message: "Devices" }),
        keywords: [
          msg({ id: "settings.keywords.devices.tablet", message: "tablet" }),
          msg({ id: "settings.keywords.devices.employeeApp", message: "employee app" }),
          msg({ id: "settings.keywords.devices.code", message: "setup code" }),
        ],
      },
      {
        path: "/settings/capacity",
        label: msg({ id: "settings.nav.capacity", message: "Capacity" }),
        keywords: [
          msg({ id: "settings.keywords.capacity.occupancy", message: "occupancy" }),
          msg({ id: "settings.keywords.capacity.thresholds", message: "thresholds" }),
          msg({ id: "settings.keywords.capacity.staffingRatio", message: "staffing ratio" }),
        ],
      },
    ],
  },
  {
    label: msg({ id: "settings.navGroup.pedagogy", message: "Children & pedagogy" }),
    items: [
      {
        path: "/settings/habits",
        label: msg({ id: "settings.nav.habits", message: "Eating habits" }),
        keywords: [
          msg({ id: "settings.keywords.habits.diet", message: "diet" }),
          msg({ id: "settings.keywords.habits.meals", message: "meals" }),
        ],
      },
      {
        path: "/settings/settling-in",
        label: msg({ id: "settings.nav.settlingIn", message: "Settling-In" }),
        keywords: [
          msg({ id: "settings.keywords.settlingIn.indicators", message: "indicators" }),
          msg({ id: "settings.keywords.settlingIn.costs", message: "costs" }),
        ],
      },
      {
        path: "/settings/portfolio",
        label: msg({ id: "settings.nav.portfolio", message: "Portfolio" }),
        keywords: [
          msg({ id: "settings.keywords.portfolio.developmentTalk", message: "development talk" }),
          msg({ id: "settings.keywords.portfolio.retention", message: "retention" }),
          msg({ id: "settings.keywords.portfolio.template", message: "template" }),
        ],
      },
      {
        path: "/settings/portfolio-taxonomy",
        label: msg({ id: "settings.nav.portfolioTaxonomy", message: "Portfolio taxonomy" }),
        keywords: [
          msg({ id: "settings.keywords.portfolioTaxonomy.domains", message: "domains" }),
          msg({ id: "settings.keywords.portfolioTaxonomy.competencies", message: "competencies" }),
          msg({ id: "settings.keywords.portfolioTaxonomy.scales", message: "scales" }),
        ],
      },
      {
        path: "/settings/portfolio-cadence",
        label: msg({ id: "settings.nav.portfolioCadence", message: "Documentation cadence" }),
        keywords: [
          msg({ id: "settings.keywords.portfolioCadence.frequency", message: "frequency" }),
          msg({ id: "settings.keywords.portfolioCadence.reminder", message: "reminder" }),
        ],
      },
      {
        path: "/settings/kindergarten-dates",
        label: msg({ id: "settings.nav.kindergartenDates", message: "Kindergarten dates" }),
        keywords: [
          msg({ id: "settings.keywords.kindergartenDates.schoolStart", message: "school start" }),
          msg({ id: "settings.keywords.kindergartenDates.entry", message: "kindergarten entry" }),
        ],
      },
    ],
  },
  {
    label: msg({ id: "nav.sections.admissions", message: "Admissions" }),
    items: [
      {
        path: "/settings/prospect-email-templates",
        label: msg({ id: "settings.nav.prospectEmailTemplates", message: "Prospect emails" }),
        keywords: [
          msg({ id: "settings.keywords.prospectEmails.enquiry", message: "enquiry" }),
          msg({ id: "settings.keywords.prospectEmails.templates", message: "email templates" }),
          msg({ id: "settings.keywords.prospectEmails.waitingList", message: "waiting list" }),
        ],
      },
      {
        path: "/settings/prospect-intake",
        label: msg({ id: "settings.nav.prospectIntake", message: "Website integration" }),
        keywords: [
          msg({ id: "settings.keywords.prospectIntake.form", message: "enquiry form" }),
          msg({ id: "settings.keywords.prospectIntake.apiKey", message: "API key" }),
        ],
      },
    ],
  },
  {
    label: msg({ id: "settings.navGroup.staffCalendar", message: "Staff & calendar" }),
    items: [
      {
        path: "/settings/shift-templates",
        label: msg({ id: "settings.nav.shiftTemplates", message: "Shift templates" }),
        keywords: [
          msg({ id: "settings.keywords.shiftTemplates.shiftTimes", message: "shift times" }),
          msg({ id: "settings.keywords.shiftTemplates.earlyShift", message: "early shift" }),
          msg({ id: "settings.keywords.shiftTemplates.lateShift", message: "late shift" }),
        ],
      },
      {
        path: "/settings/shift-planning",
        label: msg({ id: "settings.nav.shiftPlanning", message: "Shift planning" }),
        keywords: [
          msg({ id: "settings.keywords.shiftPlanning.restTime", message: "rest time" }),
          msg({ id: "settings.keywords.shiftPlanning.workingHours", message: "working hours" }),
          msg({ id: "settings.keywords.shiftPlanning.publish", message: "publish" }),
        ],
      },
      {
        path: "/settings/absences",
        label: msg({ id: "settings.nav.absences", message: "Absences" }),
        keywords: [
          msg({ id: "settings.keywords.absences.sickNote", message: "sick note" }),
          msg({ id: "settings.keywords.absences.certificate", message: "doctor's certificate" }),
        ],
      },
      {
        path: "/settings/vacation-entitlement-schedule",
        label: msg({ id: "settings.nav.vacationEntitlement", message: "Vacation entitlement" }),
        keywords: [
          msg({ id: "settings.keywords.vacationEntitlement.holidays", message: "holidays" }),
          msg({ id: "settings.keywords.vacationEntitlement.vacationDays", message: "vacation days" }),
          msg({ id: "settings.keywords.vacationEntitlement.annualLeave", message: "annual leave" }),
        ],
      },
      {
        path: "/settings/public-holidays",
        label: msg({ id: "settings.nav.publicHolidays", message: "Public holidays" }),
        keywords: [
          msg({ id: "settings.keywords.publicHolidays.bankHolidays", message: "bank holidays" }),
          msg({ id: "settings.keywords.publicHolidays.closingDays", message: "closing days" }),
        ],
      },
    ],
  },
  {
    label: msg({ id: "settings.navGroup.billing", message: "Billing" }),
    items: [
      {
        path: "/settings/finance",
        label: msg({ id: "settings.nav.finance", message: "Billing" }),
        keywords: [
          msg({ id: "settings.keywords.finance.iban", message: "IBAN" }),
          msg({ id: "settings.keywords.finance.invoicePrefix", message: "invoice prefix" }),
          msg({ id: "settings.keywords.finance.serviceCodes", message: "service codes" }),
          msg({ id: "settings.keywords.finance.qrBill", message: "QR bill" }),
        ],
      },
      {
        path: "/settings/subsidy-offices",
        label: msg({ id: "settings.nav.subsidyOffices", message: "Subsidy offices" }),
        keywords: [
          msg({ id: "settings.keywords.subsidyOffices.municipality", message: "municipality" }),
          msg({ id: "settings.keywords.subsidyOffices.authority", message: "authority" }),
        ],
      },
      {
        path: "/settings/subsidy-rates",
        label: msg({ id: "settings.nav.subsidyRates", message: "Subsidy rates" }),
        keywords: [
          msg({ id: "settings.keywords.subsidyRates.amounts", message: "subsidy amounts" }),
          msg({ id: "settings.keywords.subsidyRates.incomeBrackets", message: "income brackets" }),
        ],
      },
      {
        path: "/settings/dunning",
        label: msg({ id: "settings.nav.dunning", message: "Dunning" }),
        keywords: [
          msg({ id: "settings.keywords.dunning.reminderFees", message: "reminder fees" }),
          msg({ id: "settings.keywords.dunning.stages", message: "stages" }),
          msg({ id: "settings.keywords.dunning.paymentReminder", message: "payment reminder" }),
        ],
      },
      {
        path: "/settings/matching-rules",
        label: msg({ id: "settings.nav.matchingRules", message: "Matching rules" }),
        keywords: [
          msg({ id: "settings.keywords.matchingRules.bankStatement", message: "bank statement" }),
          msg({ id: "settings.keywords.matchingRules.paymentMatching", message: "payment matching" }),
          msg({ id: "settings.keywords.matchingRules.reconciliation", message: "reconciliation" }),
        ],
      },
    ],
  },
  {
    label: msg({ id: "settings.navGroup.communication", message: "Communication" }),
    items: [
      {
        path: "/settings/messaging",
        label: msg({ id: "settings.nav.messaging", message: "Messaging" }),
        keywords: [
          msg({ id: "settings.keywords.messaging.replyTemplates", message: "reply templates" }),
          msg({ id: "settings.keywords.messaging.quickReplies", message: "quick replies" }),
        ],
      },
      {
        path: "/settings/change-requests",
        label: msg({ id: "settings.nav.changeRequests", message: "Change requests" }),
        keywords: [
          msg({ id: "settings.keywords.changeRequests.approval", message: "approval" }),
          msg({ id: "settings.keywords.changeRequests.parentRequests", message: "parent requests" }),
          msg({ id: "settings.keywords.changeRequests.dataChanges", message: "data changes" }),
        ],
      },
      {
        path: "/settings/workflow-deadlines",
        label: msg({ id: "settings.nav.workflowDeadlines", message: "Workflow deadlines" }),
        keywords: [
          msg({ id: "settings.keywords.workflowDeadlines.overdue", message: "overdue" }),
          msg({ id: "settings.keywords.workflowDeadlines.noticePeriod", message: "notice period" }),
          msg({ id: "settings.keywords.workflowDeadlines.accident", message: "accident" }),
          msg({ id: "settings.keywords.workflowDeadlines.careRatio", message: "care ratio" }),
          msg({ id: "settings.keywords.workflowDeadlines.understaffing", message: "understaffing" }),
        ],
      },
    ],
  },
  {
    label: msg({ id: "settings.navGroup.data", message: "Data & records" }),
    items: [
      {
        path: "/settings/labels",
        label: msg({ id: "settings.nav.labels", message: "Labels" }),
        keywords: [
          msg({ id: "settings.keywords.labels.tags", message: "tags" }),
          msg({ id: "settings.keywords.labels.markers", message: "markers" }),
        ],
      },
      {
        path: "/settings/customization",
        label: msg({ id: "settings.nav.customizations", message: "Customizations" }),
        keywords: [
          msg({ id: "settings.keywords.customization.customFields", message: "custom fields" }),
          msg({ id: "settings.keywords.customization.dropdownValues", message: "dropdown values" }),
          msg({ id: "settings.keywords.customization.lists", message: "lists" }),
        ],
      },
      {
        path: "/settings/audit",
        label: msg({ id: "auditHistory.title", message: "Audit History" }),
        keywords: [
          msg({ id: "settings.keywords.audit.changeLog", message: "change log" }),
          msg({ id: "settings.keywords.audit.history", message: "who changed what" }),
        ],
      },
    ],
  },
];

/** The rail label of a settings page; every sub-page titles itself with it so rail and heading never drift. */
export function settingsNavLabel(path: string): MessageDescriptor {
  for (const group of NAV_GROUPS) {
    const item = group.items.find((candidate) => candidate.path === path);
    if (item) return item.label;
  }
  throw new Error(`settingsNav: no settings page "${path}"`);
}
